// src/pages/admin/CourseLectures/lectures/AuPairLecture02CultureProfile.tsx
import React, { useState, useRef } from "react";
import { InfoBox, AgendaCard, TaskList } from "../../components/LectureUI";

// ── Shared Accordion ──────────────────────────────────────────────
const Accordion: React.FC<{
  id: string;
  num: string;
  icon: string;
  title: string;
  openId: string;
  onToggle: (id: string) => void;
  children: React.ReactNode;
}> = ({ id, num, icon, title, openId, onToggle, children }) => {
  const isOpen = openId === id;
  const ref = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    onToggle(id);
    if (!isOpen) {
      setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  };

  return (
    <div
      ref={ref}
      className={`mb-3 rounded-xl border overflow-hidden shadow-sm ${
        isOpen ? "border-[#009fe3]/50" : "border-gray-200"
      } bg-white`}
    >
      <button
        onClick={handleToggle}
        className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors ${
          isOpen ? "bg-blue-50" : "hover:bg-gray-50"
        }`}
      >
        <div
          className={`flex-shrink-0 flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-colors ${
            isOpen ? "bg-[#009fe3] text-white" : "bg-gray-100 text-gray-500"
          }`}
        >
          <span>{icon}</span>
          <span className="hidden sm:inline">{num}</span>
        </div>
        <span
          className={`flex-1 text-sm font-bold ${
            isOpen ? "text-[#0369a1]" : "text-gray-800"
          }`}
        >
          {title}
        </span>
        <span
          className={`flex-shrink-0 text-[#009fe3] transition-transform duration-200 text-sm ${
            isOpen ? "rotate-180" : ""
          }`}
        >
          ▾
        </span>
      </button>
      {isOpen && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
};

// ── Culture Compare Row ───────────────────────────────────────────
const CultureRow: React.FC<{
  topic: string;
  bangladesh: string;
  germany: string;
}> = ({ topic, bangladesh, germany }) => (
  <div className="rounded-xl border border-gray-200 overflow-hidden">
    <div className="bg-gray-100 px-3 py-1.5">
      <p className="text-[11px] font-bold text-gray-600 uppercase tracking-wide">
        {topic}
      </p>
    </div>
    <div className="grid grid-cols-2 divide-x divide-gray-200">
      <div className="px-3 py-2 bg-green-50">
        <p className="text-[10px] font-bold text-green-600 mb-0.5">
          🇧🇩 Bangladesh
        </p>
        <p className="text-xs text-gray-700">{bangladesh}</p>
      </div>
      <div className="px-3 py-2 bg-blue-50">
        <p className="text-[10px] font-bold text-blue-600 mb-0.5">🇩🇪 Germany</p>
        <p className="text-xs text-gray-700">{germany}</p>
      </div>
    </div>
  </div>
);

// ── Photo Placeholder Card ────────────────────────────────────────
const PhotoCard: React.FC<{
  type: "good" | "bad" | "todo";
  title: string;
  description: string;
  tips: string[];
}> = ({ type, title, description, tips }) => {
  const styles = {
    good: {
      border: "border-green-300",
      header: "bg-green-500",
      badge: "✅ Good Example",
      bg: "bg-green-50",
      tipColor: "text-green-700",
      iconBg: "bg-green-100",
      icon: "📸",
    },
    bad: {
      border: "border-red-300",
      header: "bg-red-500",
      badge: "❌ Avoid This",
      bg: "bg-red-50",
      tipColor: "text-red-700",
      iconBg: "bg-red-100",
      icon: "🚫",
    },
    todo: {
      border: "border-amber-300",
      header: "bg-amber-500",
      badge: "📌 To Do",
      bg: "bg-amber-50",
      tipColor: "text-amber-700",
      iconBg: "bg-amber-100",
      icon: "🖼️",
    },
  };

  const s = styles[type];

  return (
    <div className={`rounded-xl border-2 overflow-hidden ${s.border}`}>
      {/* Header bar */}
      <div
        className={`${s.header} px-3 py-1.5 flex items-center justify-between`}
      >
        <p className="text-xs font-bold text-white">{s.badge}</p>
        <span className="text-sm">{s.icon}</span>
      </div>

      {/* Photo placeholder area */}
      <div
        className={`${s.bg} flex flex-col items-center justify-center py-8 px-4 border-b border-dashed ${s.border}`}
      >
        <div
          className={`w-16 h-16 rounded-full ${s.iconBg} flex items-center justify-center mb-3`}
        >
          <span className="text-3xl">{s.icon}</span>
        </div>
        <p className="text-xs font-bold text-gray-600 text-center mb-0.5">
          {title}
        </p>
        <p className="text-[11px] text-gray-500 text-center">{description}</p>
        <div
          className={`mt-3 px-3 py-1 rounded-full border ${s.border} ${s.bg}`}
        >
          <p className={`text-[10px] font-bold ${s.tipColor}`}>
            Photo to be added here
          </p>
        </div>
      </div>

      {/* Tips */}
      <div className="px-3 py-2.5 bg-white">
        <div className="space-y-1">
          {tips.map((tip, i) => (
            <div key={i} className="flex items-start gap-1.5">
              <span
                className={`flex-shrink-0 font-bold text-[11px] mt-0.5 ${s.tipColor}`}
              >
                {type === "bad" ? "✗" : "›"}
              </span>
              <p className={`text-[11px] ${s.tipColor}`}>{tip}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Profile Field ─────────────────────────────────────────────────
const ProfileField: React.FC<{
  icon: string;
  label: string;
  example: string;
  important?: boolean;
}> = ({ icon, label, example, important }) => (
  <div
    className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${
      important ? "bg-amber-50 border-amber-200" : "bg-gray-50 border-gray-200"
    }`}
  >
    <span className="text-base flex-shrink-0 mt-0.5">{icon}</span>
    <div className="flex-1 min-w-0">
      <p
        className={`text-sm font-bold ${
          important ? "text-amber-800" : "text-gray-800"
        }`}
      >
        {label}
      </p>
      <code className="inline-block mt-1 text-[11px] bg-white border border-gray-200 rounded px-1.5 py-0.5 text-[#0369a1] font-mono">
        {example}
      </code>
    </div>
  </div>
);

// ── Main Component ────────────────────────────────────────────────
const AuPairLecture02CultureProfile: React.FC = () => {
  const [openSection, setOpenSection] = useState<string>("");
  const toggle = (id: string) =>
    setOpenSection((prev) => (prev === id ? "" : id));

  return (
    <div>
      {/* Course Header Banner */}
      <div className="bg-gradient-to-r from-[#009fe3] to-[#0369a1] rounded-xl px-4 py-4 mb-4">
        <p className="text-[10px] font-bold uppercase tracking-widest text-white opacity-70 mb-1">
          Au Pair Preparatory Course
        </p>
        <p className="text-base font-extrabold text-white">
          Session 02 — Culture, Photos & Au Pair Profile
        </p>
        <p className="text-xs text-white opacity-80 mt-1">
          Designed by Guideline2Germany
        </p>
      </div>

      {/* Recap */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-4 mb-4">
        <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-2">
          📋 Tasks from Session 01 — done?
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[
            "Eligibility self-check completed",
            "German level honestly assessed",
            "Goethe-Institut A2 course researched",
            "Memorized: Ich möchte Au Pair in Deutschland werden",
          ].map((t, i) => (
            <div
              key={i}
              className="flex items-center gap-1.5 text-xs text-amber-800"
            >
              <span className="w-4 h-4 rounded border border-amber-400 flex-shrink-0 bg-white" />
              {t}
            </div>
          ))}
        </div>
      </div>

      <AgendaCard
        items={[
          { num: "1", label: "Cultural Differences — Germany vs Bangladesh" },
          { num: "2", label: "Daily Life Culture — What Will Surprise You" },
          { num: "3", label: "Photos — Children & Household Work" },
          { num: "4", label: "Profile Photo — Good vs Bad Examples" },
          { num: "5", label: "Motivation — Why Au Pair? Children Experience" },
          { num: "6", label: "Au Pair Profile — How to Build It" },
        ]}
      />

      {/* 01 — Cultural Differences */}
      <Accordion
        id="01"
        num="SECTION 01"
        icon="🌍"
        title="Cultural Differences — Germany vs Bangladesh"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Culture shock is one of the biggest challenges for Au Pairs. The more
          you know before you arrive, the better you can adapt. These
          differences are not wrong — they are just different.
        </p>
        <div className="space-y-3">
          <CultureRow
            topic="Family Structure"
            bangladesh="Extended family — grandparents, aunts, uncles all involved in childcare"
            germany="Nuclear family — parents only. Privacy is highly valued. Extended family visits are planned."
          />
          <CultureRow
            topic="Children & Discipline"
            bangladesh="Adults have strong authority. Children are expected to be quiet and obedient."
            germany="Children are encouraged to express opinions, ask questions, and say 'No'. This is normal."
          />
          <CultureRow
            topic="Punctuality"
            bangladesh="Flexible time culture — arriving 30 min late is common and acceptable"
            germany="Punctuality is a core value. Being 5 minutes late without notice is considered rude."
          />
          <CultureRow
            topic="Communication Style"
            bangladesh="Indirect communication — avoid conflict openly, speak politely even when unhappy"
            germany="Direct communication — people say exactly what they mean. Not rude, just honest."
          />
          <CultureRow
            topic="Food & Mealtimes"
            bangladesh="Rice-based meals, shared dishes, flexible meal times as a family"
            germany="Structured mealtimes, bread-heavy diet, children eat separately sometimes"
          />
          <CultureRow
            topic="Privacy & Space"
            bangladesh="Shared spaces, always someone around — being alone feels unusual"
            germany="Personal space is sacred. Knocking before entering is mandatory — even for children."
          />
          <CultureRow
            topic="Religion in Daily Life"
            bangladesh="Religion woven into daily routine — prayer times, halal food, modest dress"
            germany="Secular daily life — religion is private. Halal food not always available at home."
          />
        </div>
        <InfoBox
          variant="warning"
          icon="💡"
          title="Most Important Mindset Shift"
        >
          In Germany, a child saying <strong>"Nein, ich will nicht"</strong>{" "}
          (No, I don't want to) is not disrespect. German parents encourage
          this. Do not correct the child — adapt to the family's approach.
        </InfoBox>
      </Accordion>

      {/* 02 — Daily Life Culture */}
      <Accordion
        id="02"
        num="SECTION 02"
        icon="🏡"
        title="Daily Life Culture — What Will Surprise You"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          These are the small daily things that catch most Au Pairs completely
          off guard. Read them now so they do not shock you later.
        </p>
        <div className="space-y-2">
          {[
            {
              icon: "🛒",
              title: "Supermarkets close early",
              desc: "Most shops close at 20:00. On Sundays, almost everything is closed. Plan your week.",
              color: "bg-blue-50 border-blue-200 text-blue-800",
            },
            {
              icon: "♻️",
              title: "Strict recycling rules",
              desc: "Germany has Gelber Sack (yellow bag), Biomüll (organic), Restmüll (general), and glass bins. You will be expected to sort waste correctly.",
              color: "bg-green-50 border-green-200 text-green-800",
            },
            {
              icon: "🤫",
              title: "Quiet hours (Ruhezeit)",
              desc: "No loud noise between 22:00–07:00 and on Sundays. No vacuuming, no loud music. This is taken seriously.",
              color: "bg-purple-50 border-purple-200 text-purple-800",
            },
            {
              icon: "👟",
              title: "No shoes inside the house",
              desc: "Shoes are always left at the door. House slippers (Hausschuhe) will usually be provided for you.",
              color: "bg-amber-50 border-amber-200 text-amber-800",
            },
            {
              icon: "🤝",
              title: "Greetings are formal at first",
              desc: "Always say Guten Morgen, Guten Tag, or Guten Abend. Wait to be invited to use first names (Du) — especially with parents.",
              color: "bg-teal-50 border-teal-200 text-teal-800",
            },
            {
              icon: "🍽️",
              title: "Eating together is important",
              desc: "Family meals are a bonding time. Be present, engage, do not scroll your phone during meals.",
              color: "bg-orange-50 border-orange-200 text-orange-800",
            },
            {
              icon: "🏦",
              title: "Cash is still common",
              desc: "Germany uses cash more than Bangladesh. Not all shops accept card. Always carry some cash.",
              color: "bg-gray-50 border-gray-200 text-gray-800",
            },
          ].map((item, i) => (
            <div key={i} className={`rounded-xl border p-3 ${item.color}`}>
              <div className="flex items-start gap-2.5">
                <span className="text-xl flex-shrink-0">{item.icon}</span>
                <div>
                  <p className="text-sm font-bold mb-0.5">{item.title}</p>
                  <p className="text-xs opacity-80">{item.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Accordion>

      {/* 03 — Photos: Children & Household */}
      <Accordion
        id="03"
        num="SECTION 03"
        icon="📸"
        title="Photos — Children & Household Work (Example Photos)"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 mb-4">
          <p className="text-sm font-bold text-amber-800 mb-1">
            📌 Why do you need these photos?
          </p>
          <p className="text-xs text-amber-700">
            Host families want to see you in action — not just a formal
            portrait. Photos of you with children and doing household tasks make
            your profile real, warm and trustworthy. They answer the question:{" "}
            <strong>"Can this person actually do the job?"</strong>
          </p>
        </div>

        {/* Children Photos */}
        <p className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-2">
          📷 Category 1 — Photos with Children
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <PhotoCard
            type="good"
            title="Playing with a child — outdoor"
            description="You at a park, playground or garden with a child. Natural smile, engaged."
            tips={[
              "Natural setting — park, garden, indoor play",
              "You are actively engaged with the child",
              "Warm, genuine smile — not posed",
              "Child's face can be blurred if needed",
            ]}
          />
          <PhotoCard
            type="good"
            title="Reading or doing activity with child"
            description="Reading a book, drawing, or doing a puzzle together."
            tips={[
              "Shows patience and care",
              "Indoor setting is fine here",
              "Good lighting — not too dark",
              "Focus on interaction, not just faces",
            ]}
          />
          <PhotoCard
            type="bad"
            title="Group photo — child barely visible"
            description="A crowded family photo where you are in the background."
            tips={[
              "Host family cannot identify you clearly",
              "Shows no direct interaction with child",
              "Avoid selfies with children from a distance",
              "Avoid formal event photos",
            ]}
          />
          <PhotoCard
            type="todo"
            title="Your To-Do Example Photo"
            description="Take a photo with a younger sibling, cousin, or neighbor's child."
            tips={[
              "Ask permission from the child's parents first",
              "Natural activity — not a forced pose",
              "Good lighting, clean background",
              "Submit to G2G for review before uploading to profile",
            ]}
          />
        </div>

        {/* Household Photos */}
        <p className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-2">
          🏠 Category 2 — Household Work Photos
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <PhotoCard
            type="good"
            title="Cooking or helping in the kitchen"
            description="You preparing food, cutting vegetables, or setting the table."
            tips={[
              "Clean kitchen background",
              "Apron is a nice touch — looks responsible",
              "Warm lighting, natural expression",
              "Shows domestic competence",
            ]}
          />
          <PhotoCard
            type="good"
            title="Tidying, cleaning or organizing"
            description="You folding laundry, sweeping, or organizing a room."
            tips={[
              "Light, tidy room in background",
              "Shows you are capable and responsible",
              "Do not look unhappy — genuine expression",
              "Casual clothes are fine",
            ]}
          />
          <PhotoCard
            type="bad"
            title="Posed photo in formal clothes"
            description="Standing stiffly in a saree or formal dress in the kitchen."
            tips={[
              "Looks unnatural and staged",
              "Host families want to see you working, not posing",
              "Formal dress suggests you never actually do this",
              "Avoid heavy makeup in household photos",
            ]}
          />
          <PhotoCard
            type="todo"
            title="Your To-Do Household Photo"
            description="Cook a meal at home or help with household tasks — photograph it."
            tips={[
              "Ask a family member to take the photo naturally",
              "Casual, everyday clothes — no formal outfit",
              "Take 3–5 photos, pick the most natural one",
              "Submit to G2G for review before uploading",
            ]}
          />
        </div>
      </Accordion>

      {/* 04 — Profile Photo */}
      <Accordion
        id="04"
        num="SECTION 04"
        icon="🤳"
        title="Profile Photo — Good vs Bad Examples"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Your profile photo is the very first thing a host family sees. It must
          be warm, friendly, and professional — not a passport photo, not a
          party photo.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <PhotoCard
            type="good"
            title="Ideal Profile Photo"
            description="Bright, natural photo — smiling, looking into camera, clean background."
            tips={[
              "Natural daylight or bright indoor light",
              "Genuine, warm smile — approachable",
              "Clean, simple background (white wall, garden)",
              "Casual but neat clothing",
              "Face clearly visible — no sunglasses or hat",
            ]}
          />
          <PhotoCard
            type="bad"
            title="What to Avoid"
            description="Dark selfie, group photo, formal portrait, or filtered social media photo."
            tips={[
              "No dark or blurry photos",
              "No heavy Snapchat or Instagram filters",
              "No group photos — host family must identify you",
              "No formal ID-style passport photo",
              "No photos with sunglasses or heavy makeup",
            ]}
          />
        </div>

        <div className="rounded-xl border border-[#009fe3]/40 bg-[#e0f2fe] px-4 py-3">
          <p className="text-xs font-bold text-[#0369a1] mb-2">
            📐 Technical requirements for the profile photo
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { label: "Format", value: "JPG or PNG" },
              { label: "Size", value: "Under 5 MB" },
              { label: "Orientation", value: "Portrait preferred" },
              { label: "Background", value: "Plain / light color" },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-lg border border-blue-100 px-2 py-2 text-center"
              >
                <p className="text-[10px] text-blue-500 font-bold mb-0.5">
                  {item.label}
                </p>
                <p className="text-xs font-bold text-[#0369a1]">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </Accordion>

      {/* 05 — Motivation */}
      <Accordion
        id="05"
        num="SECTION 05"
        icon="💬"
        title="Motivation — Why Au Pair? Children Experience"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="rounded-xl border border-purple-200 bg-purple-50 p-3 mb-4">
          <p className="text-sm font-bold text-purple-800 mb-1">
            Host families ask this first: "Why do you want to be an Au Pair?"
          </p>
          <p className="text-xs text-purple-700">
            Your answer must be genuine, specific, and show real love for
            children — not just a desire to go to Germany. A weak answer here
            ends applications immediately.
          </p>
        </div>

        <p className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-2">
          Strong vs Weak Motivations
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div className="rounded-xl border border-green-200 bg-green-50 p-3">
            <p className="text-xs font-bold text-green-800 mb-2">
              ✅ Strong Motivation (Say this)
            </p>
            <div className="space-y-1.5">
              {[
                "I have cared for my younger siblings since I was a teenager",
                "I genuinely enjoy playing with and teaching children",
                "I want to experience German family life and culture firsthand",
                "I want to improve my German while contributing meaningfully",
                "I see Au Pair as a step toward working with children professionally",
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 bg-white/60 rounded-lg px-2 py-1"
                >
                  <span className="text-green-500 font-bold flex-shrink-0 mt-0.5">
                    ✓
                  </span>
                  <p className="text-xs text-green-700">{item}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-red-200 bg-red-50 p-3">
            <p className="text-xs font-bold text-red-800 mb-2">
              ❌ Weak Motivation (Never say this)
            </p>
            <div className="space-y-1.5">
              {[
                "I want to go to Germany and explore Europe",
                "I need money and a place to stay",
                "My friend did it so I want to try too",
                "I could not find a job in Bangladesh",
                "I want to improve my English (it is a German program!)",
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 bg-white/60 rounded-lg px-2 py-1"
                >
                  <span className="text-red-500 font-bold flex-shrink-0 mt-0.5">
                    ✗
                  </span>
                  <p className="text-xs text-red-700">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <p className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-2">
          Your Experience with Children — What Counts
        </p>
        <div className="space-y-2 mb-4">
          {[
            {
              icon: "👦",
              title: "Younger siblings or cousins",
              desc: "Did you help raise or care for a younger brother, sister or cousin? This is real childcare experience. Write it.",
              color: "bg-blue-50 border-blue-200 text-blue-800",
            },
            {
              icon: "📚",
              title: "Tutoring or teaching",
              desc: "Did you teach neighborhood children or help them with homework? This shows patience and communication skills.",
              color: "bg-purple-50 border-purple-200 text-purple-800",
            },
            {
              icon: "🕌",
              title: "Madrasa or community childcare",
              desc: "Helping at a religious school, community program, or any organized activity with children counts.",
              color: "bg-teal-50 border-teal-200 text-teal-800",
            },
            {
              icon: "🏥",
              title: "Babysitting or informal care",
              desc: "Even informal babysitting for neighbors or relatives counts. Mention how long and how often.",
              color: "bg-green-50 border-green-200 text-green-800",
            },
          ].map((item, i) => (
            <div key={i} className={`rounded-xl border p-3 ${item.color}`}>
              <div className="flex items-start gap-2.5">
                <span className="text-xl flex-shrink-0">{item.icon}</span>
                <div>
                  <p className="text-sm font-bold mb-0.5">{item.title}</p>
                  <p className="text-xs opacity-80">{item.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-xl border border-[#009fe3]/40 bg-[#e0f2fe] p-4">
          <p className="text-xs font-bold text-[#0369a1] mb-2">
            ✏️ Example Motivation Letter Sentence
          </p>
          <p className="text-xs text-[#0369a1] italic">
            "I have cared for my two younger siblings since the age of 14,
            helping with meals, school preparation and daily routines. This
            experience taught me patience, responsibility and genuine joy in
            working with children. I am excited to bring this care to a German
            family while fully immersing myself in the language and culture."
          </p>
        </div>
      </Accordion>

      {/* 06 — Au Pair Profile */}
      <Accordion
        id="06"
        num="SECTION 06"
        icon="👤"
        title="Au Pair Profile — How to Build It"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Your Au Pair profile is your application — it is what host families
          read before deciding to contact you. Every field must be filled
          completely, honestly, and in warm and friendly language.
        </p>

        <p className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-2">
          Essential Profile Fields
        </p>
        <div className="space-y-2 mb-4">
          <ProfileField
            icon="👤"
            label="Full Name & Age"
            example="Fatima Rahman, 22 years old, Dhaka, Bangladesh"
          />
          <ProfileField
            icon="📝"
            label="About Me (Introduction)"
            example="Friendly, responsible, and patient — I love spending time with children..."
            important
          />
          <ProfileField
            icon="👶"
            label="Childcare Experience"
            example="Cared for 2 younger siblings for 6 years. Tutored 3 neighborhood children."
            important
          />
          <ProfileField
            icon="🏠"
            label="Household Skills"
            example="Cooking (Bangladeshi and basic Western), cleaning, laundry, grocery shopping"
          />
          <ProfileField
            icon="🗣️"
            label="Language Skills"
            example="Bangla (native), English (B2), German (A2 — currently studying)"
            important
          />
          <ProfileField
            icon="📅"
            label="Availability"
            example="Available from: March 2025 — Duration: 12 months preferred"
          />
          <ProfileField
            icon="🎨"
            label="Hobbies & Interests"
            example="Reading, cooking, drawing with children, outdoor activities, music"
          />
          <ProfileField
            icon="🙏"
            label="Special Needs / Preferences"
            example="Prefer non-smoking family. Halal food or ability to cook own meals preferred."
          />
        </div>

        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-3">
          <p className="text-sm font-bold text-amber-800 mb-2">
            ⭐ Profile Writing Tips
          </p>
          <div className="space-y-1.5">
            {[
              "Write in the first person — I love, I enjoy, I have experience",
              "Be specific — not 'I like children' but 'I cared for my 5-year-old cousin every afternoon'",
              "Keep a warm and personal tone — host families are choosing a family member, not an employee",
              "Mention your German learning journey — shows commitment",
              "Get a native English speaker or G2G to review it before publishing",
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-2 bg-white/60 rounded-lg px-2 py-1"
              >
                <span className="text-amber-500 font-bold flex-shrink-0 mt-0.5">
                  ›
                </span>
                <p className="text-xs text-amber-700">{item}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-xs font-bold text-red-800 mb-1">
            ❌ Common Profile Mistakes
          </p>
          <div className="space-y-1">
            {[
              "Leaving the 'About Me' section empty or one sentence long",
              "Copying a template from the internet — host families have seen them all",
              "Saying 'I have no experience with children' — dig deeper, you always have some",
              "Using a dark selfie or formal passport photo as profile picture",
            ].map((item, i) => (
              <p
                key={i}
                className="text-xs text-red-700 flex items-start gap-1.5"
              >
                <span className="flex-shrink-0 mt-0.5">›</span> {item}
              </p>
            ))}
          </div>
        </div>
      </Accordion>

      {/* Summary */}
      <Accordion
        id="fazit"
        num="SUMMARY"
        icon="🏁"
        title="Session Summary"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
          <span className="text-2xl flex-shrink-0">✅</span>
          <div>
            <p className="text-sm font-bold text-green-800 mb-2">
              You now understand culture, photos and how to build a strong Au
              Pair profile.
            </p>
            <ul className="space-y-1">
              {[
                "German culture is direct, punctual, and privacy-focused — adapt to it",
                "Children saying 'Nein' is normal in Germany — do not correct them",
                "Shoes off at the door, quiet hours, strict recycling — know the rules",
                "Photos must show you actively working — not posing",
                "Profile photo: warm, natural, bright — no filters, no group photos",
                "Motivation must center on genuine love for children — not Germany travel",
                "Document ALL childcare experience — siblings, cousins, tutoring all count",
                "Profile must be specific, warm, and personal — not a template",
              ].map((item, i) => (
                <li
                  key={i}
                  className="text-sm text-green-700 flex items-start gap-2"
                >
                  <span className="flex-shrink-0 mt-0.5">›</span> {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Accordion>

      {/* Tasks */}
      <Accordion
        id="aufgaben"
        num="TASKS"
        icon="📋"
        title="Your Tasks — Session 02"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="pt-1">
          <TaskList
            tasks={[
              <>
                <strong>Take a children photo</strong> — with a younger sibling,
                cousin or neighbor's child doing a natural activity. Submit to
                G2G for review.
              </>,
              <>
                <strong>Take a household work photo</strong> — cooking, cleaning
                or organizing at home. Natural clothes, good lighting.
              </>,
              <>
                <strong>Take your profile photo</strong> — bright light, warm
                smile, plain background. No filters.
              </>,
              <>
                <strong>Write your Motivation paragraph</strong> — 3–5 sentences
                about why you want to be an Au Pair and your experience with
                children. Submit to G2G for feedback.
              </>,
              <>
                <strong>Start drafting your Au Pair profile</strong> — fill in
                all fields listed in Section 06. Bring it to the next session.
              </>,
            ]}
          />
        </div>
      </Accordion>
    </div>
  );
};

export default AuPairLecture02CultureProfile;
